import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from 'record-it-components/hooks/useEventCallback';
import { FilterPipelineFilter } from 'record-it-components/hooks/useFilterPipeline/useFilterPipeline';
import { useSpecificParams } from 'record-it-components/hooks/useSpecificParams/useSpecificParams';
import { HeaderConfig, ManageEntitiesContentLayout } from 'record-it-components/pageContentLayout/ManageEntitiesContentLayout/ManageEntitiesContentLayout';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { EntityName } from 'record-it-time-common/EntityName';
import { TimeTrackingActivityCompilation } from '../../../classes/entities/TimeTrackingActivityCompilation/timeTrackingActivityCompilationTypes';
import { iconConfigurations } from '../../../classes/icon/iconConfigurations';
import { RoutePaths } from '../../../classes/RoutePaths/RoutePaths';
import { createUserGroupIdUrlFilter } from '../../../filters/userGroupIdUrlFilter/userGroupIdUrlFilter';
import { useEntities } from '../../../hooks/entity/useEntities/useEntities';
import { useEntityRepository } from '../../../hooks/entity/useEntityRepository';
import { useConfigureBreadcrumbsWithBuilder } from '../../../hooks/misc/useConfigureBreadcrumbsWithBuilder';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { useEmptyCreateEntityButtonConfig } from '../../../hooks/useEmptyCreateEntityButtonConfig';
import { TimeTrackingActivityCompilationList } from '../../timeTrackingActivityCompilation/TimeTrackingActivityCompilationList/TimeTrackingActivityCompilationList';
export type TimeTrackingActivityCompilationAdminPageProps = PropsWithoutChildren<Record<never, never>>;
export const TimeTrackingActivityCompilationAdminPage: FunctionComponent<TimeTrackingActivityCompilationAdminPageProps> = React.memo(props => {
  const $ = _c(25);
  prepareProps({
    props,
    ignoreChildren: true
  });
  const {
    getRequiredCurrentUserPermissions
  } = useCurrentUser();
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      requiredParamNames: ["userGroupId"]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    userGroupId
  } = useSpecificParams(t0);
  let t1;
  if ($[1] !== userGroupId) {
    t1 = builder => builder.addHomePage().addTimeTrackingActivityCompilationAdminPage(userGroupId);
    $[1] = userGroupId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  useConfigureBreadcrumbsWithBuilder(t1);
  const activities = useEntities(EntityName.TimeTrackingActivityCompilation, "getByUserGroupId", userGroupId);
  const repository = useEntityRepository(EntityName.TimeTrackingActivityCompilation);
  let t2;
  if ($[3] !== repository || $[4] !== userGroupId) {
    t2 = () => {
      repository.create({
        name: "",
        ownerUserGroupId: userGroupId
      });
    };
    $[3] = repository;
    $[4] = userGroupId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleCreateEntity = useEventCallback(t2);
  let t3;
  if ($[6] !== t) {
    t3 = t("components.page.TimeTrackingActivityCompilationAdminPage.createEntity");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== handleCreateEntity || $[9] !== t3) {
    t4 = {
      onCreateEntity: handleCreateEntity,
      text: t3
    };
    $[8] = handleCreateEntity;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const createEntityButtonConfig = useEmptyCreateEntityButtonConfig(t4);
  let t5;
  if ($[11] !== t) {
    t5 = t("components.page.TimeTrackingActivityCompilationAdminPage.pageTitle");
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = {
      iconConfig: iconConfigurations.prefixed.page.TimeTrackingActivityCompilationAdminPage.titleIcon,
      title: t5
    };
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const headerConfig = t6;
  let t7;
  if ($[15] !== getRequiredCurrentUserPermissions || $[16] !== userGroupId) {
    const permissions = getRequiredCurrentUserPermissions();
    t7 = createUserGroupIdUrlFilter({
      userGroups: permissions.getEditableUserGroupsForTimeTrackingActivityCompilations(),
      currentUserGroupId: userGroupId,
      createUrl: _temp
    });
    $[15] = getRequiredCurrentUserPermissions;
    $[16] = userGroupId;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t7) {
    t8 = [t7];
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const filters = t8;
  const renderEntities = _temp2;
  let t9;
  if ($[20] !== activities || $[21] !== createEntityButtonConfig || $[22] !== filters || $[23] !== headerConfig) {
    t9 = <ManageEntitiesContentLayout createEntityButtonConfig={createEntityButtonConfig} headerConfig={headerConfig} entities={activities} renderEntities={renderEntities} usePagination={false} filters={filters} />;
    $[20] = activities;
    $[21] = createEntityButtonConfig;
    $[22] = filters;
    $[23] = headerConfig;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
});
function _temp(urlUserGroupId) {
  return RoutePaths.adminTimeTrackingActivityCompilations(urlUserGroupId);
}
function _temp2(currentPageCompilations) {
  return <TimeTrackingActivityCompilationList timeTrackingActivityCompilations={currentPageCompilations} data-testid="TimeTrackingActivityCompilationAdminPage.ActivityList" />;
}